import { z } from 'zod';
import { insuranceClaimsCommandReasonSchema, insuranceClaimsCommandReceiptSchema, insuranceClaimsIdSchema, } from './insurance-claims-claim.contract.js';
export const insuranceClaimsAdjusterSlaCapability = 'insurance-claims.adjuster-sla.v1';
export const insuranceClaimsAdjusterSlaOverrideVersionsCapability = 'insurance-claims.adjuster-sla-override-versions.v1';
export const insuranceClaimsAdjusterCorrespondenceFactsCapability = 'communications.adjuster-correspondence-facts.v1';
export const insuranceClaimsAdjusterCorrespondenceProviderSlot = 'insurance-claims.adjuster-correspondence-facts';
export const insuranceClaimsAdjusterCorrespondenceProviderId = 'communications.server.insurance-claims-adjuster-correspondence';
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
export const insuranceClaimsAdjusterSlaSeveritySchema = z.enum([
    'normal', 'medium', 'high', 'critical',
]);
export const insuranceClaimsAdjusterSlaAttentionStateSchema = z.enum([
    'waiting-on-team', 'waiting-on-adjuster',
]);
export const insuranceClaimsAdjusterSlaOverrideActionSchema = z.enum([
    'snoozed', 'acknowledged', 'escalated', 'cleared',
]);
export const insuranceClaimsAdjusterCorrespondenceProviderRequestSchema = z.strictObject({
    consumerModuleId: z.literal('insurance-claims'),
    requestedCapability: z.literal(insuranceClaimsAdjusterCorrespondenceFactsCapability),
    resourceType: z.literal('insurance-claims.claim'),
    resourceId: insuranceClaimsIdSchema,
    preferredSourceId: insuranceClaimsIdSchema,
    projectId: insuranceClaimsIdSchema,
});
export const insuranceClaimsAdjusterCorrespondenceProviderFactSchema = z.strictObject({
    capability: z.literal(insuranceClaimsAdjusterCorrespondenceFactsCapability),
    threadId: insuranceClaimsIdSchema,
    sourceVersion: z.number().int().positive(),
    contentHash: sha256Schema,
    projectId: insuranceClaimsIdSchema,
    contactId: insuranceClaimsIdSchema,
    subject: nullableText(240),
    latestMessage: z.strictObject({
        messageId: insuranceClaimsIdSchema,
        direction: z.enum(['inbound', 'outbound']),
        occurredAt: z.string().datetime({ offset: true }),
    }),
    lastOutboundAt: z.string().datetime({ offset: true }).nullable(),
    observedAt: z.string().datetime({ offset: true }),
});
export const insuranceClaimsAdjusterCorrespondenceProviderResponseSchema = z.strictObject({
    state: z.literal('present'),
    capability: z.literal(insuranceClaimsAdjusterCorrespondenceFactsCapability),
    projectId: insuranceClaimsIdSchema,
    contactId: insuranceClaimsIdSchema,
    facts: z.array(insuranceClaimsAdjusterCorrespondenceProviderFactSchema).max(500),
}).superRefine((value, context) => {
    const threadIds = value.facts.map(({ threadId }) => threadId);
    if (new Set(threadIds).size !== threadIds.length) {
        context.addIssue({ code: 'custom', path: ['facts'], message: 'Correspondence threads must be unique.' });
    }
    value.facts.forEach((fact, index) => {
        if (fact.projectId !== value.projectId || fact.contactId !== value.contactId) {
            context.addIssue({
                code: 'custom',
                path: ['facts', index],
                message: 'Correspondence facts must belong to the requested Project and contact.',
            });
        }
    });
});
export const insuranceClaimsAdjusterCorrespondenceUnavailableReasonSchema = z.enum([
    'not-installed',
    'not-enabled',
    'permission-denied',
    'adjuster-contact-not-linked',
    'provider-invalid',
    'provider-failed',
]);
export const insuranceClaimsAdjusterCorrespondenceFactSchema = z.strictObject({
    capability: z.literal(insuranceClaimsAdjusterCorrespondenceFactsCapability),
    threadId: insuranceClaimsIdSchema,
    sourceVersion: z.number().int().positive(),
    contentHash: sha256Schema,
    claimId: insuranceClaimsIdSchema,
    projectId: insuranceClaimsIdSchema,
    subject: nullableText(240),
    adjuster: z.strictObject({
        displayName: z.string().trim().min(1).max(240),
        relationshipContactId: insuranceClaimsIdSchema.nullable(),
    }),
    latestMessage: z.strictObject({
        messageId: insuranceClaimsIdSchema,
        direction: z.enum(['inbound', 'outbound']),
        occurredAt: z.string().datetime({ offset: true }),
    }),
    lastOutboundAt: z.string().datetime({ offset: true }).nullable(),
    observedAt: z.string().datetime({ offset: true }),
});
export const insuranceClaimsAdjusterCorrespondenceAvailabilitySchema = z.discriminatedUnion('state', [
    z.strictObject({
        state: z.literal('unavailable'),
        capability: z.literal(insuranceClaimsAdjusterCorrespondenceFactsCapability),
        reasonCode: insuranceClaimsAdjusterCorrespondenceUnavailableReasonSchema,
        reason: z.string().trim().min(1).max(300),
    }),
    z.strictObject({
        state: z.literal('present'),
        capability: z.literal(insuranceClaimsAdjusterCorrespondenceFactsCapability),
        facts: z.array(insuranceClaimsAdjusterCorrespondenceFactSchema).max(500),
    }),
]);
export const insuranceClaimsAdjusterSlaFactReferenceSchema = z.strictObject({
    capability: z.literal(insuranceClaimsAdjusterCorrespondenceFactsCapability),
    threadId: insuranceClaimsIdSchema,
    sourceVersion: z.number().int().positive(),
    contentHash: sha256Schema,
    observedAt: z.string().datetime({ offset: true }),
});
export const insuranceClaimsAdjusterSlaOverrideVersionDataSchema = z.strictObject({
    factReference: insuranceClaimsAdjusterSlaFactReferenceSchema,
    action: insuranceClaimsAdjusterSlaOverrideActionSchema,
    snoozeUntil: z.string().datetime({ offset: true }).nullable(),
    notes: nullableText(1_000),
}).superRefine((value, context) => {
    if (value.action === 'snoozed' && value.snoozeUntil === null) {
        context.addIssue({
            code: 'custom', path: ['snoozeUntil'], message: 'Snoozed SLA overrides require a wake time.',
        });
    }
    if (value.action !== 'snoozed' && value.snoozeUntil !== null) {
        context.addIssue({
            code: 'custom', path: ['snoozeUntil'], message: 'Only a snoozed SLA override has a wake time.',
        });
    }
});
export const insuranceClaimsAdjusterSlaOverrideVersionSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsAdjusterSlaOverrideVersionsCapability),
    tenantId: insuranceClaimsIdSchema,
    id: insuranceClaimsIdSchema,
    overrideId: insuranceClaimsIdSchema,
    versionNumber: z.number().int().positive(),
    contentHash: sha256Schema,
    reason: insuranceClaimsCommandReasonSchema,
    actorMembershipId: insuranceClaimsIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    data: insuranceClaimsAdjusterSlaOverrideVersionDataSchema,
});
export const insuranceClaimsAdjusterSlaOverrideSchema = z.strictObject({
    tenantId: insuranceClaimsIdSchema,
    id: insuranceClaimsIdSchema,
    claimId: insuranceClaimsIdSchema,
    projectId: insuranceClaimsIdSchema,
    threadId: insuranceClaimsIdSchema,
    revision: z.number().int().positive(),
    currentVersionId: insuranceClaimsIdSchema,
    createdByMembershipId: insuranceClaimsIdSchema,
    updatedByMembershipId: insuranceClaimsIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    currentVersion: insuranceClaimsAdjusterSlaOverrideVersionSchema,
});
export const insuranceClaimsSetAdjusterSlaOverrideCommandSchema = z.strictObject({
    kind: z.literal('set-override'),
    commandId: insuranceClaimsIdSchema,
    overrideId: insuranceClaimsIdSchema,
    claimId: insuranceClaimsIdSchema,
    threadId: insuranceClaimsIdSchema,
    expectedRevision: z.number().int().nonnegative(),
    factReference: insuranceClaimsAdjusterSlaFactReferenceSchema,
    action: z.enum(['snoozed', 'acknowledged', 'escalated']),
    snoozeUntil: z.string().datetime({ offset: true }).nullable(),
    notes: nullableText(1_000),
    reason: insuranceClaimsCommandReasonSchema,
}).superRefine((value, context) => {
    if (value.action === 'snoozed' && value.snoozeUntil === null) {
        context.addIssue({
            code: 'custom', path: ['snoozeUntil'], message: 'Snoozed SLA overrides require a wake time.',
        });
    }
    if (value.action !== 'snoozed' && value.snoozeUntil !== null) {
        context.addIssue({
            code: 'custom', path: ['snoozeUntil'], message: 'Only a snoozed SLA override has a wake time.',
        });
    }
});
export const insuranceClaimsClearAdjusterSlaOverrideCommandSchema = z.strictObject({
    kind: z.literal('clear-override'),
    commandId: insuranceClaimsIdSchema,
    overrideId: insuranceClaimsIdSchema,
    claimId: insuranceClaimsIdSchema,
    threadId: insuranceClaimsIdSchema,
    expectedRevision: z.number().int().positive(),
    factReference: insuranceClaimsAdjusterSlaFactReferenceSchema,
    reason: insuranceClaimsCommandReasonSchema,
});
export const insuranceClaimsAdjusterSlaCommandSchema = z.discriminatedUnion('kind', [
    insuranceClaimsSetAdjusterSlaOverrideCommandSchema,
    insuranceClaimsClearAdjusterSlaOverrideCommandSchema,
]);
export const insuranceClaimsAdjusterSlaItemSchema = z.strictObject({
    threadId: insuranceClaimsIdSchema,
    claimId: insuranceClaimsIdSchema,
    projectId: insuranceClaimsIdSchema,
    factReference: insuranceClaimsAdjusterSlaFactReferenceSchema,
    subject: nullableText(240),
    adjuster: insuranceClaimsAdjusterCorrespondenceFactSchema.shape.adjuster,
    latestMessage: insuranceClaimsAdjusterCorrespondenceFactSchema.shape.latestMessage,
    lastOutboundAt: z.string().datetime({ offset: true }).nullable(),
    attentionState: insuranceClaimsAdjusterSlaAttentionStateSchema,
    elapsedMinutes: z.number().int().nonnegative(),
    targetAt: z.string().datetime({ offset: true }).nullable(),
    breached: z.boolean(),
    severity: insuranceClaimsAdjusterSlaSeveritySchema,
    visible: z.boolean(),
    override: insuranceClaimsAdjusterSlaOverrideSchema.nullable(),
});
export const insuranceClaimsAdjusterSlaProjectionSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsAdjusterSlaCapability),
    claimId: insuranceClaimsIdSchema,
    projectId: insuranceClaimsIdSchema,
    asOf: z.string().datetime({ offset: true }),
    provider: z.discriminatedUnion('state', [
        z.strictObject({ state: z.literal('available') }),
        z.strictObject({
            state: z.literal('unavailable'),
            capability: z.literal(insuranceClaimsAdjusterCorrespondenceFactsCapability),
            reasonCode: insuranceClaimsAdjusterCorrespondenceUnavailableReasonSchema,
            reason: z.string().trim().min(1).max(300),
        }),
    ]),
    policy: z.strictObject({
        targetHours: z.number().int().min(1).max(720),
        highHours: z.number().int().min(1).max(720),
        criticalHours: z.number().int().min(1).max(720),
    }),
    summary: z.strictObject({
        totalBreached: z.number().int().nonnegative(),
        medium: z.number().int().nonnegative(),
        high: z.number().int().nonnegative(),
        critical: z.number().int().nonnegative(),
        waitingOnAdjuster: z.number().int().nonnegative(),
        oldestMinutes: z.number().int().nonnegative(),
    }),
    items: z.array(insuranceClaimsAdjusterSlaItemSchema),
});
export const insuranceClaimsAdjusterSlaResponseSchema = z.strictObject({
    data: insuranceClaimsAdjusterSlaProjectionSchema,
    command: insuranceClaimsCommandReceiptSchema.optional(),
});
export function deriveInsuranceClaimsAdjusterSlaProjection(input) {
    const parsed = z.strictObject({
        claimId: insuranceClaimsIdSchema,
        projectId: insuranceClaimsIdSchema,
        asOf: z.string().datetime({ offset: true }),
        targetHours: z.number().int().min(1).max(720),
        highHours: z.number().int().min(1).max(720),
        criticalHours: z.number().int().min(1).max(720),
    }).superRefine((value, context) => {
        if (!(value.targetHours < value.highHours && value.highHours < value.criticalHours)) {
            context.addIssue({ code: 'custom', message: 'SLA thresholds must increase strictly.' });
        }
    }).parse({
        claimId: input.claimId,
        projectId: input.projectId,
        asOf: input.asOf,
        targetHours: input.targetHours,
        highHours: input.highHours,
        criticalHours: input.criticalHours,
    });
    const overrideByThread = new Map(input.overrides.map((override) => [override.threadId, override]));
    const asOfMs = new Date(parsed.asOf).getTime();
    const facts = input.source.state === 'present' ? input.source.facts : [];
    const items = facts.map((fact) => {
        const occurredMs = new Date(fact.latestMessage.occurredAt).getTime();
        const elapsedMinutes = Math.max(0, Math.floor((asOfMs - occurredMs) / 60_000));
        const attentionState = fact.latestMessage.direction === 'inbound'
            ? 'waiting-on-team'
            : 'waiting-on-adjuster';
        const breached = attentionState === 'waiting-on-team' &&
            elapsedMinutes >= parsed.targetHours * 60;
        const severity = !breached
            ? 'normal'
            : elapsedMinutes >= parsed.criticalHours * 60
                ? 'critical'
                : elapsedMinutes >= parsed.highHours * 60
                    ? 'high'
                    : 'medium';
        const override = overrideByThread.get(fact.threadId) ?? null;
        const currentAction = override?.currentVersion.data.action ?? null;
        const snoozeUntil = override?.currentVersion.data.snoozeUntil ?? null;
        const hiddenByOverride = currentAction === 'acknowledged' ||
            (currentAction === 'snoozed' && snoozeUntil !== null && snoozeUntil > parsed.asOf);
        return insuranceClaimsAdjusterSlaItemSchema.parse({
            threadId: fact.threadId,
            claimId: fact.claimId,
            projectId: fact.projectId,
            factReference: {
                capability: fact.capability,
                threadId: fact.threadId,
                sourceVersion: fact.sourceVersion,
                contentHash: fact.contentHash,
                observedAt: fact.observedAt,
            },
            subject: fact.subject,
            adjuster: fact.adjuster,
            latestMessage: fact.latestMessage,
            lastOutboundAt: fact.lastOutboundAt,
            attentionState,
            elapsedMinutes,
            targetAt: attentionState === 'waiting-on-team'
                ? new Date(occurredMs + parsed.targetHours * 3_600_000).toISOString()
                : null,
            breached,
            severity,
            visible: !hiddenByOverride,
            override,
        });
    }).sort((left, right) => right.elapsedMinutes - left.elapsedMinutes);
    const visibleBreached = items.filter((item) => item.visible && item.breached);
    return insuranceClaimsAdjusterSlaProjectionSchema.parse({
        contractVersion: insuranceClaimsAdjusterSlaCapability,
        claimId: parsed.claimId,
        projectId: parsed.projectId,
        asOf: parsed.asOf,
        provider: input.source.state === 'present'
            ? { state: 'available' }
            : {
                state: 'unavailable',
                capability: input.source.capability,
                reasonCode: input.source.reasonCode,
                reason: input.source.reason,
            },
        policy: {
            targetHours: parsed.targetHours,
            highHours: parsed.highHours,
            criticalHours: parsed.criticalHours,
        },
        summary: {
            totalBreached: visibleBreached.length,
            medium: visibleBreached.filter(({ severity }) => severity === 'medium').length,
            high: visibleBreached.filter(({ severity }) => severity === 'high').length,
            critical: visibleBreached.filter(({ severity }) => severity === 'critical').length,
            waitingOnAdjuster: items.filter((item) => item.visible &&
                item.attentionState === 'waiting-on-adjuster').length,
            oldestMinutes: visibleBreached[0]?.elapsedMinutes ?? 0,
        },
        items,
    });
}
//# sourceMappingURL=insurance-claims-adjuster-sla.contract.js.map